import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SectionHeader from "../SectionHeader";
import Card from "../Card";
import DrawByDrawBillingTable from "../DrawByDrawBillingTable";
import { Draw } from "../DrawHistoryTable";
import Table, { Column, GroupHeaderCell } from "Components/Table/Table";
import Button from "Components/Common/Button/Button";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import { exportProjectAiaBillingSummary } from "services/projectService";
import { triggerBlobDownload } from "utils/Common/fileDownload";
import { useAiaBillingSummary } from "hooks/useAiaBillingSummary";
import {
  AiaG703LineItem,
  AiaG703Flag,
  MoneyValue,
} from "hooks/useProjectAiaSummary";
import { useAiaPdf } from "hooks/useAiaPdf";

interface AiaBillingSectionProps {
  isActive: boolean;
  projectId: string;
  projectName?: string;
}

const amountOf = (money?: MoneyValue | null) => Number(money?.amount ?? 0);

const formatMoney = (money?: MoneyValue | null): string => {
  if (!money || money.amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: money.currency?.code || "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(money.amount ?? 0));
};

const formatMoneyPrecise = (money?: MoneyValue | null): string => {
  if (!money || money.amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: money.currency?.code || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(money.amount ?? 0));
};

const formatPercent = (value?: number | string) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "0%";
  return `${Number.isInteger(num) ? num : num.toFixed(2)}%`;
};

const getPctClass = (pct?: number) => {
  const n = Number(pct ?? 0);
  if (n === 100) return "pctHigh";
  if (n === 0) return "pctZero";
  if (n < 30) return "pctWarn";
  return "";
};

const PctCell = ({ pct }: { pct?: number }) => {
  const n = Math.min(100, Math.max(0, Number(pct ?? 0)));
  const cls = getPctClass(n);

  return (
    <>
      <span className="pctBarWrap">
        <span className={`pctBar ${cls}`} style={{ width: `${n / 2}px` }} />
      </span>
      <span className={`pctNum ${cls}`}>{formatPercent(n)}</span>
    </>
  );
};
const G703_SKELETON_ROWS = Array.from({ length: 6 }, (_, i) => i);

const AiaBillingSection = ({
  isActive,
  projectId,
  projectName,
}: AiaBillingSectionProps) => {
  const navigate = useNavigate();
  const { summary, loading, error } = useAiaBillingSummary(
    projectId,
    "gc",
    isActive,
  );
  const { exportAiaPdf, isExportingPdf } = useAiaPdf();
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const g702 = summary?.g702;
  const g703Rows = useMemo<AiaG703LineItem[]>(
    () => summary?.g703?.lineItems ?? [],
    [summary],
  );

  const draws = useMemo<Draw[]>(
    () =>
      (summary?.drawHistory ?? []).map((d) => ({
        id: d.drawId,
        drawNumber: d.drawNumber,
        status: d.status,
        toDate: d.periodTo,
        requestedAmount: {
          amount: d.amountBilled?.amount ?? undefined,
          currency: d.amountBilled?.currency ?? undefined,
        },
        stored: {
          amount: d.stored?.amount ?? undefined,
          currency: d.stored?.currency ?? undefined,
        },
      })),
    [summary],
  );

  const latestDrawNumber = summary?.meta?.asOfDrawNumber ?? null;

  const g702Metrics = useMemo(
    () =>
      g702
        ? [
            {
              label: "Line 3",
              value: formatMoney(g702.contractSumToDate),
              variant: "blue",
              sub: "Contract sum to date",
            },
            {
              label: "Line 4",
              value: formatMoney(g702.totalCompletedAndStored),
              sub: "Total completed & stored",
            },
            {
              label: "Line 5",
              value: formatMoney(g702.totalRetainage),
              variant: "red",
              sub: `Retainage (${g702.retainageRate ?? "—"}%)`,
            },
            {
              label: "Line 8",
              value: formatMoney(g702.currentPaymentDue),
              variant: "green",
              sub: "Current payment due",
            },
            {
              label: "Line 9",
              value: formatMoney(g702.balanceToFinish),
              variant: "amber",
              sub: "Balance to finish",
            },
          ]
        : [],
    [g702],
  );

  const totals = useMemo(() => {
    const acc = g703Rows.reduce(
      (a, row) => ({
        sched: a.sched + amountOf(row.sovValue),
        prev: a.prev + amountOf(row.previousBilled),
        curr: a.curr + amountOf(row.currentApplication),
        stored: a.stored + amountOf(row.currentStoredMaterial),
        total: a.total + amountOf(row.totalCompletedAndStored),
        remain: a.remain + amountOf(row.balanceToFinish),
      }),
      { sched: 0, prev: 0, curr: 0, stored: 0, total: 0, remain: 0 },
    );
    const currency =
      g703Rows[0]?.sovValue?.currency ?? g702?.contractSumToDate?.currency;
    const pct = acc.sched > 0 ? (acc.total / acc.sched) * 100 : 0;
    const wrap = (amount: number): MoneyValue => ({ amount, currency });
    return {
      sched: wrap(acc.sched),
      prev: wrap(acc.prev),
      curr: wrap(acc.curr),
      stored: wrap(acc.stored),
      total: wrap(acc.total),
      remain: wrap(acc.remain),
      pct,
    };
  }, [g702, g703Rows]);

  const g703GroupHeaderRow: GroupHeaderCell[] = [
    { label: "Item Identification", colSpan: 3, className: "aiaColHeadLeft" },
    { label: "AIA G703 Columns", colSpan: 7, className: "aiaColHead" },
  ];

  const g703Columns: Column<AiaG703LineItem>[] = [
    {
      header: "#",
      className: "center",
      render: (row, i) =>
        row.itemNo ?? row.aiaItem?.itemLabel ?? row.aiaItemCode ?? i + 1,
    },
    {
      header: "Description of Work",
      className: "left",
      render: (row) => (
        <>
          {row.description ?? row.aiaItem?.itemName ?? "—"}
          {row.hasOpenFlags && <span className="flagBadge">⚠ FLAGGED</span>}
        </>
      ),
    },
    {
      header: "Subcontractor",
      className: "left",
      render: (row) => row.subContractor?.companyName ?? "—",
    },
    {
      header: (
        <>
          Col C
          <br />
          Sched. Value
        </>
      ),
      className: "right",
      render: (row) => formatMoneyPrecise(row.sovValue),
    },
    {
      header: (
        <>
          Col D
          <br />
          Prev. Apps
        </>
      ),
      className: "right",
      render: (row) => formatMoneyPrecise(row.previousBilled),
    },
    {
      header: (
        <>
          Col E
          <br />
          This Period
        </>
      ),
      className: "right",
      render: (row) => formatMoneyPrecise(row.currentApplication),
    },
    {
      header: (
        <>
          Col F
          <br />
          Stored
        </>
      ),
      className: "right",
      render: (row) => formatMoneyPrecise(row.currentStoredMaterial),
    },
    {
      header: (
        <>
          Col G
          <br />
          Total C&amp;S
        </>
      ),
      className: "right",
      render: (row) => formatMoneyPrecise(row.totalCompletedAndStored),
    },
    {
      header: (
        <>
          Col G/C
          <br />%
        </>
      ),
      className: "right",
      render: (row) => <PctCell pct={row.percentageComplete} />,
    },
    {
      header: (
        <>
          Col I
          <br />
          Balance
        </>
      ),
      className: "right",
      render: (row) => formatMoneyPrecise(row.balanceToFinish),
    },
  ];

  // One entry per (row, flag) pair — a line item can carry flags from more
  // than one inspection, and each gets its own callout below the table.
  const flaggedNotes = useMemo(
    () =>
      g703Rows.flatMap((row) =>
        row.hasOpenFlags && row.flags?.length
          ? row.flags.map((flag: AiaG703Flag, i: number) => ({
              key: `${row.aiaItemId ?? row.itemNo}-${i}`,
              row,
              flag,
            }))
          : [],
      ),
    [g703Rows],
  );

  const formatFlagNote = (row: AiaG703LineItem, flag: AiaG703Flag): string => {
    const itemLabel = row.description ?? row.aiaItem?.itemName ?? "this item";
    const itemNo = row.itemNo ?? row.aiaItem?.itemLabel ?? row.aiaItemCode;
    const reportNo =
      flag.inspectionNumber != null
        ? String(flag.inspectionNumber).padStart(2, "0")
        : "—";
    const parts = [
      `${itemLabel}${itemNo != null ? ` (Item ${itemNo})` : ""} — CSC flagged this item in Report #${reportNo}.`,
      `GC billed ${formatPercent(row.percentageComplete)} cumulatively; CSC estimated ~${formatPercent(flag.cscEstimation)} complete based on field observation.`,
    ];
    if (flag.message) parts.push(flag.message);
    return parts.join(" ");
  };

  const g703FooterRow = [
    "",
    "Grand Total",
    "",
    formatMoneyPrecise(totals.sched),
    formatMoneyPrecise(totals.prev),
    formatMoneyPrecise(totals.curr),
    formatMoneyPrecise(totals.stored),
    formatMoneyPrecise(totals.total),
    <PctCell key="pct" pct={totals.pct} />,
    formatMoneyPrecise(totals.remain),
  ];

  const handleExportExcel = async () => {
    if (!projectId) return;
    setIsExportingExcel(true);
    try {
      const blob = await exportProjectAiaBillingSummary(projectId, "gc");
      triggerBlobDownload(
        blob,
        `AIA-Billing-Summary-${projectName ?? "export"}-${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } finally {
      setIsExportingExcel(false);
    }
  };
  const handleExportPdf = () =>
    exportAiaPdf(projectId, projectName, undefined, "gc", false);
  const handleStartDraw = () => navigate(`/draw-request/${projectId}`);

  return (
    <div className={`section ${isActive ? "active" : ""}`} id="sec-aia-billing">
      <SectionHeader
        title="AIA Billing Summary"
        subtitle={`Your running AIA G702/G703 continuation sheet — all draws to date by SOV line item.${
          latestDrawNumber
            ? ` Through Draw #${String(latestDrawNumber).padStart(2, "0")}.`
            : ""
        }`}
      />

      <div className="aiaBillingInfo">
        <strong>This is a read-only summary</strong> of your billing history.
        Certified values come from bank-approved draws. To submit a new draw,
        use the{" "}
        <Button
          type="button"
          className="aiaInlineLink"
          onClick={handleStartDraw}
        >
          Draw Request
        </Button>{" "}
        workflow. Off-site stored materials (Column F) are shown separately
        where applicable.
      </div>

      <div className="aiaG702Strip aiaG702StripHeader">
        AIA G702 — Application Summary
        {latestDrawNumber
          ? ` Through Draw #${String(latestDrawNumber).padStart(2, "0")}`
          : ""}
      </div>
      <div className="aiaG702Strip">
        <div className="aiaG702Cells">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div className="aiaG702Cell" key={i}>
                  <Skeleton height={44} borderRadius={6} />
                </div>
              ))
            : g702Metrics.map((line) => (
                <div className="aiaG702Cell" key={line.label}>
                  <div className="aiaG702Num">{line.label}</div>
                  <div className={`aiaG702Val ${line.variant ?? ""}`.trim()}>
                    {line.value}
                  </div>
                  <div className="aiaG702Sub">{line.sub}</div>
                </div>
              ))}
        </div>
      </div>

      <Card
        title="AIA G703 — Continuation Sheet"
        action={
          <div style={{ display: "flex", gap: 6 }}>
            <Button
              type="button"
              className="btn secondary sm"
              onClick={handleExportExcel}
              disabled={isExportingExcel || loading || g703Rows.length === 0}
            >
              {isExportingExcel ? "Generating…" : "📊 Export .xlsx"}
            </Button>
            <Button
              type="button"
              className="btn secondary sm"
              onClick={handleExportPdf}
              disabled={isExportingPdf || loading}
            >
              {isExportingPdf ? "Generating…" : "📄 Export PDF"}
            </Button>
          </div>
        }
      >
        {loading ? (
          <TableSkeleton cols={10} rows={G703_SKELETON_ROWS.length} />
        ) : error ? (
          <div className="badge b-review">{error}</div>
        ) : (
          <div className="aiaTableWrap">
            <Table
              tableClassName="aiaTable"
              data={g703Rows}
              columns={g703Columns}
              groupHeaderRow={g703GroupHeaderRow}
              footerRow={g703Rows.length > 0 ? g703FooterRow : undefined}
              rowClassName={(row) => (row.hasOpenFlags ? "flaggedRow" : "")}
            />
          </div>
        )}

        <div className={flaggedNotes.length > 0 ? "twoCol" : undefined}>
          <div className="aiaColumnKey">
            <strong>Column Key:</strong> Column Key: C = Scheduled Value · D =
            Work Completed from Previous Applications · E = Work Completed This
            Period · F = Materials Presently Stored (not yet incorporated) · G =
            Total Completed & Stored (D+E+F) · G/C = % · I = Balance to Finish
            (C–G)
          </div>

          {flaggedNotes.length > 0 && (
            <div className="aiaFlaggedNoteWrap">
              {flaggedNotes.map(({ key, row, flag }) => (
                <div className="aiaFlaggedNote" key={key}>
                  <strong>⚠ Flagged Item:</strong> {formatFlagNote(row, flag)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="billingActions">
          <Button
            type="button"
            className="btn primary"
            onClick={handleStartDraw}
          >
            + Start New Draw Request
          </Button>
          <Button
            type="button"
            className="btn secondary"
            onClick={handleExportExcel}
            disabled={isExportingExcel || loading || g703Rows.length === 0}
          >
            📊 Download Full G703 (.xlsx)
          </Button>
          <Button
            type="button"
            className="btn secondary"
            onClick={handleExportPdf}
            disabled={isExportingPdf || loading}
          >
            📄 Download as PDF
          </Button>
        </div>
      </Card>

      <DrawByDrawBillingTable
        draws={draws}
        contractSumToDate={g702?.contractSumToDate?.amount ?? undefined}
        loading={loading}
      />
    </div>
  );
};

export default AiaBillingSection;
